<template>
  <v-container fluid>
    <v-row>
      <v-col cols="12">
        <v-card class="elevation-1">
          <v-card-title class="d-flex align-center justify-space-between pa-6">
            <div class="text-h4 font-weight-bold">Gestión de Grupos</div>
            <div class="header-actions">
              <v-btn
                @click="openCreateDialog"
                color="primary"
                size="large"
                class="create-user-btn"
                prepend-icon="mdi-account-group"
              >
                Crear Grupo
              </v-btn>
            </div>
          </v-card-title>
          <v-card-text class="pa-6">
            <div class="text-subtitle-1">
              Administra los grupos de usuarios. Puedes crear, editar, activar o desactivar grupos
              según sea necesario.
            </div>
          </v-card-text>

          <v-divider></v-divider>

          <!-- Filters Section -->
          <v-card-text>
            <v-row class="align-start">
              <v-col cols="12" md="4">
                <v-text-field
                  v-model="searchQuery"
                  label="Buscar grupos..."
                  prepend-inner-icon="mdi-magnify"
                  variant="outlined"
                  density="compact"
                  clearable
                  @input="debounceSearch"
                />
              </v-col>
              <v-col cols="12" md="3">
                <v-select
                  v-model="statusFilter"
                  label="Estado"
                  :items="statusOptions"
                  variant="outlined"
                  density="compact"
                  clearable
                  @update:model-value="applyFilters"
                />
              </v-col>
              <v-col cols="12" md="3" class="d-flex flex-column justify-end">
                <v-btn
                  variant="outlined"
                  color="primary"
                  @click="resetFilters"
                  class="text-none"
                  block
                >
                  Limpiar filtros
                </v-btn>
              </v-col>
            </v-row>
          </v-card-text>

          <!-- Groups Table -->
          <v-data-table
            :headers="headers"
            :items="allGroups"
            :loading="loading"
            :items-per-page="pagination.perPage"
            hide-default-footer
            class="elevation-0"
          >
            <!-- School Grade Column -->
            <template #item.school_grade="{ item }">
              <v-chip
                color="primary"
                :text="item.school_grade ? `${item.school_grade.grade}° Grado` : 'N/A'"
                size="small"
                variant="tonal"
              />
            </template>

            <!-- Status Column -->
            <template #item.status="{ item }">
              <v-chip
                :color="item.is_active ? 'success' : 'error'"
                :text="item.is_active ? 'Activo' : 'Inactivo'"
                size="small"
                variant="tonal"
              />
            </template>

            <!-- PIN Count Column -->
            <template #item.pin_count="{ item }">
              <v-chip color="info" :text="item.pin_count || 0" size="small" variant="tonal" />
            </template>

            <!-- Created Date Column -->
            <template #item.created_at="{ item }">
              {{ formatDate(item.created_at) }}
            </template>

            <!-- Actions Column -->
            <template #item.actions="{ item }">
              <div class="d-flex ga-2">
                <v-tooltip location="top">
                  <template #activator="{ props }">
                    <v-btn
                      icon="mdi-eye"
                      size="small"
                      variant="tonal"
                      color="info"
                      v-bind="props"
                      @click="viewGroup(item)"
                    />
                  </template>
                  <span>Ver detalles</span>
                </v-tooltip>

                <v-tooltip location="top">
                  <template #activator="{ props }">
                    <v-btn
                      icon="mdi-pencil"
                      size="small"
                      variant="tonal"
                      color="primary"
                      v-bind="props"
                      @click="editGroup(item)"
                    />
                  </template>
                  <span>Editar Grupo</span>
                </v-tooltip>

                <v-tooltip location="top">
                  <template #activator="{ props }">
                    <v-btn
                      :icon="item.is_active ? 'mdi-archive' : 'mdi-restore'"
                      size="small"
                      variant="tonal"
                      :color="item.is_active ? 'warning' : 'success'"
                      v-bind="props"
                      @click="toggleGroupStatus(item)"
                    />
                  </template>
                  <span>{{ item.is_active ? 'Desactivar' : 'Restaurar' }}</span>
                </v-tooltip>
              </div>
            </template>

            <!-- No data -->
            <template #no-data>
              <div class="text-center pa-4">
                <v-icon size="64" color="grey-lighten-1" class="mb-4">
                  mdi-folder-open-outline
                </v-icon>
                <div class="text-h6 text-grey">No se encontraron grupos</div>
                <div class="text-body-2 text-grey">Crea el primer grupo para iniciar</div>
              </div>
            </template>
          </v-data-table>

          <!-- Pagination -->
          <v-divider></v-divider>
          <div class="d-flex justify-center pa-4">
            <v-pagination
              v-model="currentPage"
              :length="pagination.totalPages"
              :total-visible="7"
              @update:model-value="handlePageChange"
              color="primary"
            />
          </div>
        </v-card>
      </v-col>
    </v-row>

    <!-- Create/Edit Dialog -->
    <GroupForm v-model="formDialog" :group="selectedGroup" @saved="handleGroupSaved" />

    <!-- Confirm Dialog -->
    <ConfirmDialog
      v-model="confirmDialog.show"
      :title="confirmDialog.title"
      :text="confirmDialog.text"
      :color="confirmDialog.color"
      :loading="confirmDialog.loading"
      @confirmed="handleConfirmAction"
      @cancelled="handleCancelAction"
    />

    <!-- Snackbar for notifications -->
    <v-snackbar
      v-model="snackbar.show"
      :color="snackbar.color"
      :timeout="4000"
      location="top right"
    >
      {{ snackbar.text }}
      <template #actions>
        <v-btn variant="text" @click="snackbar.show = false"> Cerrar </v-btn>
      </template>
    </v-snackbar>
  </v-container>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex'
import GroupForm from './GroupForm.vue'
import ConfirmDialog from '../common/ConfirmDialog.vue'

export default {
  name: 'GroupsList',
  components: {
    GroupForm,
    ConfirmDialog,
  },
  data() {
    return {
      searchQuery: '',
      statusFilter: null,
      searchTimeout: null,
      currentPage: 1,
      selectedGroup: null,
      formDialog: false,
      confirmDialog: {
        show: false,
        title: '',
        text: '',
        color: '',
        loading: false,
        callback: null,
      },
      snackbar: {
        show: false,
        text: '',
        color: 'success',
      },
      headers: [
        { title: 'Nombre', key: 'name', align: 'start', sortable: false },
        { title: 'Descripción', key: 'description', align: 'start', sortable: false },
        { title: 'Grado', key: 'school_grade', align: 'center', sortable: false },
        { title: 'Estado', key: 'status', align: 'center', sortable: false },
        { title: 'Fecha de creación', key: 'created_at', align: 'center', sortable: false },
        { title: 'Acciones', key: 'actions', align: 'center', sortable: false },
      ],
      statusOptions: [
        { title: 'Todos', value: null },
        { title: 'Activo', value: true },
        { title: 'Inactivo', value: false },
      ],
    }
  },
  computed: {
    ...mapState('groups', ['loading', 'error', 'pagination', 'filters']),
    ...mapGetters('groups', ['allGroups', 'isLoading']),
  },
  async mounted() {
    await this.fetchGroups()
  },
  methods: {
    ...mapActions('groups', [
      'fetchGroups',
      'deactivateGroup',
      'restoreGroup',
      'setPage',
      'setFilters',
      'clearError',
    ]),

    debounceSearch() {
      clearTimeout(this.searchTimeout)
      this.searchTimeout = setTimeout(() => {
        this.applyFilters()
      }, 500)
    },

    async applyFilters() {
      await this.setFilters({
        search: this.searchQuery,
        is_active: this.statusFilter,
      })
    },

    async resetFilters() {
      this.searchQuery = ''
      this.statusFilter = null
      await this.setFilters({
        search: '',
        is_active: null,
      })
    },

    async handlePageChange(page) {
      this.currentPage = page
      await this.setPage(page)
    },

    openCreateDialog() {
      this.selectedGroup = null
      this.formDialog = true
    },

    viewGroup(group) {
      this.$router.push({ name: 'GroupDetails', params: { id: group.id } })
    },

    editGroup(group) {
      this.selectedGroup = { ...group }
      this.formDialog = true
    },

    toggleGroupStatus(group) {
      if (group.is_active) {
        this.confirmDialog = {
          show: true,
          title: 'Desactivar Grupo',
          text: `¿Estás seguro de que quieres desactivar "${group.name}"? Esta acción se puede revertir más tarde.`,
          color: 'warning',
          callback: () => this.handleDeactivateGroup(group.id),
        }
      } else {
        this.confirmDialog = {
          show: true,
          title: 'Restaurar Grupo',
          text: `¿Estás seguro de que quieres restaurar "${group.name}"?`,
          color: 'success',
          callback: () => this.handleRestoreGroup(group.id),
        }
      }
    },

    async handleConfirmAction() {
      if (this.confirmDialog.callback) {
        this.confirmDialog.loading = true
        await this.confirmDialog.callback()
        this.confirmDialog.loading = false
        this.confirmDialog.show = false
        this.confirmDialog.callback = null
      }
    },

    handleCancelAction() {
      this.confirmDialog.show = false
      this.confirmDialog.callback = null
    },

    async handleDeactivateGroup(groupId) {
      try {
        const result = await this.deactivateGroup(groupId)
        if (result.success) {
          this.showSnackbar('Grupo desactivado exitosamente', 'success')
        } else {
          this.showSnackbar(result.error || 'Error al desactivar grupo', 'error')
        }
      } catch (error) {
        this.showSnackbar('Error al desactivar grupo', 'error')
      }
    },

    async handleRestoreGroup(groupId) {
      try {
        const result = await this.restoreGroup(groupId)
        if (result.success) {
          this.showSnackbar('Grupo restaurado exitosamente', 'success')
        } else {
          this.showSnackbar(result.error || 'Error al restaurar grupo', 'error')
        }
      } catch (error) {
        this.showSnackbar('Error al restaurar grupo', 'error')
      }
    },

    async handleGroupSaved(message) {
      this.formDialog = false
      this.selectedGroup = null
      this.showSnackbar(message, 'success')
      await this.fetchGroups()
    },

    showSnackbar(text, color = 'success') {
      this.snackbar = { show: true, text, color }
    },

    formatDate(dateString) {
      if (!dateString) return ''
      const date = new Date(dateString)
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      })
    },
  },
}
</script>

<style scoped>
.v-card-title {
  background: rgba(25, 118, 210, 0.05);
}

.text-primary {
  color: #1976d2 !important;
}

.v-data-table {
  background: transparent;
}

.v-chip {
  font-weight: 500;
}

.ga-2 {
  gap: 8px;
}
</style>
